import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Shield, Bell, Plug, CalendarClock, Send, MessageCircle,
  CheckCircle2, XCircle, Play, Trash2, Plus, RefreshCw, Sparkles, Phone
} from "lucide-react";

const TABS = [
  { key: "profile", label: "Profil", icon: Shield },
  { key: "integrations", label: "Integrasi API", icon: Plug },
  { key: "alerts", label: "Alert Channels", icon: Bell },
  { key: "schedules", label: "Laporan Terjadwal", icon: CalendarClock },
];

export default function Settings() {
  const { user } = useAuth();
  const [tab, setTab] = useState("profile");

  return (
    <div className="p-6 lg:p-8 space-y-6 fade-in">
      <div className="pb-2 border-b border-[#1F1F1F]">
        <div className="label-mono">// system settings</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2" data-testid="settings-title">
          Pengaturan <span className="text-[#00F0FF]">Sistem</span>
        </h1>
      </div>

      <div className="flex items-center gap-1 border border-[#1F1F1F] bg-[#0A0A0A] p-1 w-fit">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`settings-tab-${t.key}`}
            className={`label-mono px-4 py-2 flex items-center gap-2 transition ${
              tab === t.key ? "bg-[#007AFF] text-white" : "text-zinc-500 hover:text-white"
            }`}
          >
            <t.icon size={12} />
            {t.label}
          </button>
        ))}
      </div>

      {tab === "profile" && <ProfileTab user={user} />}
      {tab === "integrations" && <IntegrationsTab />}
      {tab === "alerts" && <AlertsTab />}
      {tab === "schedules" && <SchedulesTab />}
    </div>
  );
}

function ProfileTab({ user }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl">
      <div className="border border-[#1F1F1F] bg-[#0A0A0A] p-5">
        <Shield size={20} className="text-[#00F0FF] mb-3" />
        <h3 className="font-display font-bold text-lg">Profil</h3>
        <div className="space-y-2 mt-4 font-mono text-xs">
          <div className="flex justify-between"><span className="text-zinc-500">nama</span><span>{user?.name}</span></div>
          <div className="flex justify-between"><span className="text-zinc-500">email</span><span className="truncate ml-3">{user?.email}</span></div>
          <div className="flex justify-between"><span className="text-zinc-500">role</span><span className="text-[#00E676]">{user?.role}</span></div>
        </div>
      </div>
      <div className="border border-[#1F1F1F] bg-[#0A0A0A] p-5">
        <Bell size={20} className="text-[#FFCC00] mb-3" />
        <h3 className="font-display font-bold text-lg">Notifikasi</h3>
        <p className="text-sm text-zinc-500 mt-2">Alert aktif: traffic turun, review negatif, rating rendah, penjualan turun, engagement turun.</p>
      </div>
      <div className="border border-[#1F1F1F] bg-[#0A0A0A] p-5">
        <Plug size={20} className="text-[#B180FF] mb-3" />
        <h3 className="font-display font-bold text-lg">Tema</h3>
        <p className="text-sm text-zinc-500 mt-2">Dark Tactical Command Center — mode default sistem.</p>
      </div>
    </div>
  );
}

function IntegrationsTab() {
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/integrations");
    setItems(data.integrations);
  };
  useEffect(() => { load(); }, []);

  const open = (p) => {
    setExpanded(p);
    const item = items.find((i) => i.platform === p);
    // Prefill non-secret fields with unmasked value; leave secret blank
    const d = {};
    item.fields.forEach((f) => {
      d[f.key] = f.secret ? "" : (item.credentials_masked[f.key] || "");
    });
    setDraft(d);
  };

  const save = async (platform) => {
    setBusy(true);
    try {
      const creds = { ...draft };
      Object.keys(creds).forEach((k) => { if (creds[k] === "" || creds[k] == null) delete creds[k]; });
      await api.put(`/integrations/${platform}`, { credentials: creds, enabled: true });
      toast.success("Kredensial disimpan");
      setExpanded(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal simpan");
    } finally { setBusy(false); }
  };

  const test = async (platform) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/integrations/${platform}/test`);
      if (data.ok) toast.success("Koneksi API berhasil ✓");
      else toast.error(data.message || "Tes koneksi gagal");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Tes gagal");
    } finally { setBusy(false); }
  };

  const remove = async (platform) => {
    if (!window.confirm("Hapus semua kredensial platform ini?")) return;
    await api.delete(`/integrations/${platform}`);
    toast.success("Kredensial dihapus");
    setExpanded(null);
    load();
  };

  return (
    <div className="space-y-4 max-w-5xl" data-testid="integrations-tab">
      <div className="label-mono text-zinc-500">// kelola kredensial api untuk integrasi data real-time</div>
      {items.map((it) => (
        <div key={it.platform} className="border border-[#1F1F1F] bg-[#0A0A0A]" data-testid={`integration-${it.platform}`}>
          <div className="px-5 py-4 flex items-center justify-between border-b border-[#1F1F1F]">
            <div className="flex items-center gap-3">
              {it.connected ? <CheckCircle2 size={18} className="text-[#00E676]" /> : <XCircle size={18} className="text-zinc-600" />}
              <div>
                <div className="font-medium">{it.label}</div>
                <div className="label-mono">{it.channel_types.join(" · ")}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {it.connected && (
                <button onClick={() => test(it.platform)} disabled={busy}
                  data-testid={`integration-test-${it.platform}`}
                  className="label-mono px-3 py-1.5 border border-[#1F1F1F] hover:border-[#00E676] hover:text-[#00E676]">
                  test koneksi
                </button>
              )}
              <button onClick={() => expanded === it.platform ? setExpanded(null) : open(it.platform)}
                data-testid={`integration-config-${it.platform}`}
                className="label-mono px-3 py-1.5 border border-[#1F1F1F] hover:border-[#007AFF]">
                {expanded === it.platform ? "tutup" : it.connected ? "edit" : "hubungkan"}
              </button>
            </div>
          </div>

          {expanded === it.platform && (
            <div className="p-5 space-y-4 border-t border-[#1F1F1F] bg-[#050505]">
              {it.docs && (
                <div className="label-mono">
                  // dokumentasi:{" "}
                  <a href={it.docs} target="_blank" rel="noreferrer" className="text-[#00F0FF] hover:underline">{it.docs}</a>
                </div>
              )}
              {it.fields.map((f) => (
                <div key={f.key}>
                  <label className="label-mono block mb-2">{f.label}</label>
                  {f.type === "textarea" ? (
                    <textarea
                      value={draft[f.key] || ""}
                      onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                      placeholder={f.placeholder || ""}
                      rows={5}
                      data-testid={`integration-field-${it.platform}-${f.key}`}
                      className="w-full bg-[#0A0A0A] border border-[#1F1F1F] px-3 py-2 font-mono text-xs outline-none focus:border-[#007AFF]"
                    />
                  ) : (
                    <input
                      type={f.type === "password" ? "password" : "text"}
                      value={draft[f.key] || ""}
                      onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                      placeholder={f.secret && it.credentials_masked[f.key] ? "•••• (biarkan kosong untuk tidak mengubah)" : f.placeholder || ""}
                      data-testid={`integration-field-${it.platform}-${f.key}`}
                      className="w-full bg-[#0A0A0A] border border-[#1F1F1F] px-3 py-2.5 font-mono text-sm outline-none focus:border-[#007AFF]"
                    />
                  )}
                </div>
              ))}
              <div className="flex items-center gap-2 pt-2">
                <button onClick={() => save(it.platform)} disabled={busy}
                  data-testid={`integration-save-${it.platform}`}
                  className="bg-[#007AFF] hover:bg-[#0056b3] px-4 py-2.5 label-mono">simpan</button>
                {it.connected && (
                  <button onClick={() => remove(it.platform)} disabled={busy}
                    data-testid={`integration-delete-${it.platform}`}
                    className="border border-[#1F1F1F] hover:border-[#FF3B30] hover:text-[#FF3B30] px-4 py-2.5 label-mono">hapus kredensial</button>
                )}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function AlertsTab() {
  const [cfg, setCfg] = useState(null);
  const [emailInput, setEmailInput] = useState("");
  const [tgInput, setTgInput] = useState("");
  const [waInput, setWaInput] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/alerts/config");
    setCfg(data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/alerts/config", cfg);
      toast.success("Konfigurasi disimpan");
      load();
    } catch (err) { toast.error("Gagal simpan"); } finally { setBusy(false); }
  };

  const testChannel = async (channel) => {
    setBusy(true);
    try {
      const { data } = await api.post("/alerts/test", { channel });
      const res = data.result;
      if (res?.status === "sent") toast.success(`Test ${channel} terkirim`);
      else if (res?.status === "skipped") toast.warning(`Skipped: ${res.reason}`);
      else toast.error(`Error: ${res?.reason || "unknown"}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Test gagal"); } finally { setBusy(false); }
  };

  const sendDigest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/alerts/send-digest");
      toast.success(`Digest terkirim (${data.notification_count} alerts)`);
    } catch (err) { toast.error("Gagal kirim digest"); } finally { setBusy(false); }
  };

  const add = (key, value, resetter) => {
    const v = value.trim();
    if (!v) return;
    setCfg({ ...cfg, [key]: [...(cfg[key] || []), v] });
    resetter("");
  };
  const remove = (key, i) => setCfg({ ...cfg, [key]: cfg[key].filter((_, idx) => idx !== i) });

  if (!cfg) return <div className="label-mono text-zinc-500">memuat...</div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 max-w-6xl" data-testid="alerts-tab">
      {/* Email */}
      <div className="border border-[#1F1F1F] bg-[#0A0A0A] p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Send size={18} className="text-[#007AFF]" />
            <div>
              <h3 className="font-display font-bold">Email (Resend)</h3>
              <div className={`label-mono ${cfg.email_configured ? "text-[#00E676]" : "text-[#FF3B30]"}`}>
                {cfg.email_configured ? "● configured" : "○ api key kosong"}
              </div>
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={cfg.email_enabled}
              onChange={(e) => setCfg({ ...cfg, email_enabled: e.target.checked })}
              data-testid="alerts-email-toggle" />
            <span className="label-mono">aktif</span>
          </label>
        </div>
        <div className="flex gap-2">
          <input value={emailInput} onChange={(e) => setEmailInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add("email_recipients", emailInput, setEmailInput))}
            placeholder="admin@perusahaan.co.id"
            data-testid="alerts-email-input"
            className="flex-1 bg-[#050505] border border-[#1F1F1F] px-3 py-2 font-mono text-sm outline-none focus:border-[#007AFF]" />
          <button onClick={() => add("email_recipients", emailInput, setEmailInput)}
            data-testid="alerts-email-add"
            className="px-3 border border-[#1F1F1F] hover:border-[#007AFF] label-mono"><Plus size={12} /></button>
        </div>
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {(cfg.email_recipients || []).map((e, i) => (
            <div key={i} className="flex items-center justify-between bg-[#050505] px-3 py-2 border border-[#1F1F1F]">
              <span className="font-mono text-xs truncate">{e}</span>
              <button onClick={() => remove("email_recipients", i)} className="text-zinc-500 hover:text-[#FF3B30]"><Trash2 size={12} /></button>
            </div>
          ))}
          {(cfg.email_recipients || []).length === 0 && <div className="label-mono text-zinc-600">belum ada recipient</div>}
        </div>
        <button onClick={() => testChannel("email")} disabled={busy || !cfg.email_recipients?.length}
          data-testid="alerts-email-test"
          className="w-full label-mono px-3 py-2 border border-[#1F1F1F] hover:border-[#00E676] hover:text-[#00E676] disabled:opacity-30">
          test email
        </button>
      </div>

      {/* WhatsApp Fonnte */}
      <div className="border border-[#1F1F1F] bg-[#0A0A0A] p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Phone size={18} className="text-[#25D366]" />
            <div>
              <h3 className="font-display font-bold">WhatsApp (Fonnte)</h3>
              <div className={`label-mono ${cfg.whatsapp_configured ? "text-[#00E676]" : "text-[#FF3B30]"}`}>
                {cfg.whatsapp_configured ? "● token configured" : "○ fonnte token kosong"}
              </div>
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={cfg.whatsapp_enabled}
              onChange={(e) => setCfg({ ...cfg, whatsapp_enabled: e.target.checked })}
              data-testid="alerts-wa-toggle" />
            <span className="label-mono">aktif</span>
          </label>
        </div>
        <div className="flex gap-2">
          <input value={waInput} onChange={(e) => setWaInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add("wa_recipients", waInput, setWaInput))}
            placeholder="6281234567890 atau 081234567890"
            data-testid="alerts-wa-input"
            className="flex-1 bg-[#050505] border border-[#1F1F1F] px-3 py-2 font-mono text-sm outline-none focus:border-[#007AFF]" />
          <button onClick={() => add("wa_recipients", waInput, setWaInput)}
            data-testid="alerts-wa-add"
            className="px-3 border border-[#1F1F1F] hover:border-[#25D366] label-mono"><Plus size={12} /></button>
        </div>
        <div className="label-mono text-zinc-600">// format: 62xxxxxx atau 08xxxxxx (Fonnte auto-handle)</div>
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {(cfg.wa_recipients || []).map((w, i) => (
            <div key={i} className="flex items-center justify-between bg-[#050505] px-3 py-2 border border-[#1F1F1F]">
              <span className="font-mono text-xs">{w}</span>
              <button onClick={() => remove("wa_recipients", i)} className="text-zinc-500 hover:text-[#FF3B30]"><Trash2 size={12} /></button>
            </div>
          ))}
          {(cfg.wa_recipients || []).length === 0 && <div className="label-mono text-zinc-600">belum ada nomor wa</div>}
        </div>
        <button onClick={() => testChannel("whatsapp")} disabled={busy || !cfg.wa_recipients?.length}
          data-testid="alerts-wa-test"
          className="w-full label-mono px-3 py-2 border border-[#1F1F1F] hover:border-[#25D366] hover:text-[#25D366] disabled:opacity-30">
          test whatsapp
        </button>
      </div>

      {/* Telegram */}
      <div className="border border-[#1F1F1F] bg-[#0A0A0A] p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MessageCircle size={18} className="text-[#00F0FF]" />
            <div>
              <h3 className="font-display font-bold">Telegram</h3>
              <div className={`label-mono ${cfg.telegram_configured ? "text-[#00E676]" : "text-[#FF3B30]"}`}>
                {cfg.telegram_configured ? "● bot token configured" : "○ bot token kosong"}
              </div>
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={cfg.telegram_enabled}
              onChange={(e) => setCfg({ ...cfg, telegram_enabled: e.target.checked })}
              data-testid="alerts-tg-toggle" />
            <span className="label-mono">aktif</span>
          </label>
        </div>
        <div className="flex gap-2">
          <input value={tgInput} onChange={(e) => setTgInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add("telegram_chat_ids", tgInput, setTgInput))}
            placeholder="-1001234567890"
            data-testid="alerts-tg-input"
            className="flex-1 bg-[#050505] border border-[#1F1F1F] px-3 py-2 font-mono text-sm outline-none focus:border-[#007AFF]" />
          <button onClick={() => add("telegram_chat_ids", tgInput, setTgInput)}
            data-testid="alerts-tg-add"
            className="px-3 border border-[#1F1F1F] hover:border-[#007AFF] label-mono"><Plus size={12} /></button>
        </div>
        <div className="label-mono text-zinc-600">// dari @userinfobot di telegram</div>
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {(cfg.telegram_chat_ids || []).map((e, i) => (
            <div key={i} className="flex items-center justify-between bg-[#050505] px-3 py-2 border border-[#1F1F1F]">
              <span className="font-mono text-xs">{e}</span>
              <button onClick={() => remove("telegram_chat_ids", i)} className="text-zinc-500 hover:text-[#FF3B30]"><Trash2 size={12} /></button>
            </div>
          ))}
          {(cfg.telegram_chat_ids || []).length === 0 && <div className="label-mono text-zinc-600">belum ada chat id</div>}
        </div>
        <button onClick={() => testChannel("telegram")} disabled={busy || !cfg.telegram_chat_ids?.length}
          data-testid="alerts-tg-test"
          className="w-full label-mono px-3 py-2 border border-[#1F1F1F] hover:border-[#00E676] hover:text-[#00E676] disabled:opacity-30">
          test telegram
        </button>
      </div>

      <div className="lg:col-span-3 flex items-center justify-between border border-[#1F1F1F] bg-[#0A0A0A] p-4">
        <div className="label-mono text-zinc-500">
          // konfigurasi berlaku untuk digest manual & scheduled reports
        </div>
        <div className="flex items-center gap-2">
          <button onClick={sendDigest} disabled={busy}
            data-testid="alerts-send-digest"
            className="label-mono px-4 py-2 border border-[#1F1F1F] hover:border-[#00F0FF] hover:text-[#00F0FF]">
            kirim digest sekarang
          </button>
          <button onClick={save} disabled={busy}
            data-testid="alerts-save"
            className="bg-[#007AFF] hover:bg-[#0056b3] px-4 py-2 label-mono">simpan konfigurasi</button>
        </div>
      </div>
    </div>
  );
}

function SchedulesTab() {
  const [items, setItems] = useState([]);
  const [emailConfigured, setEmailConfigured] = useState(false);
  const [waConfigured, setWaConfigured] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: "Laporan AI Harian",
    cadence: "daily",
    format: "both",
    report_type: "ai",
    recipients: "",
    wa_recipients: "",
    hour: 8,
    day_of_week: "mon",
    enabled: true,
  });

  const load = async () => {
    const { data } = await api.get("/schedules");
    setItems(data.schedules || []);
    setEmailConfigured(data.email_configured);
    setWaConfigured(data.whatsapp_configured);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/schedules", {
        ...form,
        recipients: form.recipients.split(",").map(s => s.trim()).filter(Boolean),
        wa_recipients: form.wa_recipients.split(",").map(s => s.trim()).filter(Boolean),
      });
      toast.success("Jadwal dibuat");
      setShowForm(false);
      setForm({ name: "Laporan AI Harian", cadence: "daily", format: "both", report_type: "ai", recipients: "", wa_recipients: "", hour: 8, day_of_week: "mon", enabled: true });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal buat jadwal"); }
  };

  const toggle = async (s) => { await api.patch(`/schedules/${s.id}`, { enabled: !s.enabled }); load(); };
  const del = async (s) => {
    if (!window.confirm(`Hapus jadwal "${s.name}"?`)) return;
    await api.delete(`/schedules/${s.id}`);
    toast.success("Jadwal dihapus");
    load();
  };
  const runNow = async (s) => {
    try {
      await api.post(`/schedules/${s.id}/run-now`);
      toast.success("Laporan sedang dibuat & dikirim...");
      setTimeout(load, 2500);
    } catch { toast.error("Gagal kirim laporan"); }
  };

  return (
    <div className="space-y-4 max-w-5xl" data-testid="schedules-tab">
      {(!emailConfigured || !waConfigured) && (
        <div className="border border-[#FFCC00]/40 border-l-4 border-l-[#FFCC00] bg-[#FFCC00]/5 p-4 label-mono text-[#FFCC00] space-y-1">
          {!emailConfigured && <div>resend api key kosong — email tidak akan terkirim (isi RESEND_API_KEY di .env)</div>}
          {!waConfigured && <div>fonnte token kosong — whatsapp tidak akan terkirim (isi FONNTE_TOKEN di .env)</div>}
        </div>
      )}
      <div className="flex items-center justify-between">
        <div className="label-mono text-zinc-500">// {items.length} jadwal · email {emailConfigured ? "✓" : "×"} · wa {waConfigured ? "✓" : "×"}</div>
        <button onClick={() => setShowForm(!showForm)}
          data-testid="schedules-add-btn"
          className="bg-[#007AFF] hover:bg-[#0056b3] px-4 py-2 label-mono flex items-center gap-2">
          <Plus size={12} /> {showForm ? "tutup" : "buat jadwal"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="border border-[#1F1F1F] bg-[#0A0A0A] p-5 space-y-4" data-testid="schedules-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label-mono block mb-2">nama jadwal</label>
              <input required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                data-testid="schedule-name"
                className="w-full bg-[#050505] border border-[#1F1F1F] px-3 py-2 font-mono text-sm outline-none focus:border-[#007AFF]" />
            </div>
            <div>
              <label className="label-mono block mb-2 flex items-center gap-1"><Sparkles size={10} className="text-[#00F0FF]" /> tipe laporan</label>
              <select value={form.report_type} onChange={(e) => setForm({...form, report_type: e.target.value})}
                data-testid="schedule-report-type"
                className="w-full bg-[#050505] border border-[#1F1F1F] px-3 py-2 font-mono text-sm outline-none focus:border-[#007AFF]">
                <option value="ai">AI Executive Summary (Recommended)</option>
                <option value="basic">Basic — hanya lampiran</option>
              </select>
            </div>
            <div>
              <label className="label-mono block mb-2">cadence</label>
              <select value={form.cadence} onChange={(e) => setForm({...form, cadence: e.target.value})}
                data-testid="schedule-cadence"
                className="w-full bg-[#050505] border border-[#1F1F1F] px-3 py-2 font-mono text-sm outline-none focus:border-[#007AFF]">
                <option value="daily">Harian</option>
                <option value="weekly">Mingguan</option>
              </select>
            </div>
            <div>
              <label className="label-mono block mb-2">format lampiran</label>
              <select value={form.format} onChange={(e) => setForm({...form, format: e.target.value})}
                data-testid="schedule-format"
                className="w-full bg-[#050505] border border-[#1F1F1F] px-3 py-2 font-mono text-sm outline-none focus:border-[#007AFF]">
                <option value="pdf">PDF</option>
                <option value="excel">Excel</option>
                <option value="both">PDF + Excel</option>
                <option value="none">Tanpa lampiran (email body saja)</option>
              </select>
            </div>
            <div>
              <label className="label-mono block mb-2">jam kirim (WIB)</label>
              <input type="number" min={0} max={23} value={form.hour}
                onChange={(e) => setForm({...form, hour: parseInt(e.target.value)})}
                data-testid="schedule-hour"
                className="w-full bg-[#050505] border border-[#1F1F1F] px-3 py-2 font-mono text-sm outline-none focus:border-[#007AFF]" />
            </div>
            {form.cadence === "weekly" && (
              <div>
                <label className="label-mono block mb-2">hari</label>
                <select value={form.day_of_week} onChange={(e) => setForm({...form, day_of_week: e.target.value})}
                  data-testid="schedule-dow"
                  className="w-full bg-[#050505] border border-[#1F1F1F] px-3 py-2 font-mono text-sm outline-none focus:border-[#007AFF]">
                  <option value="mon">Senin</option><option value="tue">Selasa</option>
                  <option value="wed">Rabu</option><option value="thu">Kamis</option>
                  <option value="fri">Jumat</option><option value="sat">Sabtu</option>
                  <option value="sun">Minggu</option>
                </select>
              </div>
            )}
            <div className="md:col-span-2">
              <label className="label-mono block mb-2">email recipients (pisahkan koma)</label>
              <input value={form.recipients} onChange={(e) => setForm({...form, recipients: e.target.value})}
                placeholder="ceo@company.id, manager@company.id"
                data-testid="schedule-recipients"
                className="w-full bg-[#050505] border border-[#1F1F1F] px-3 py-2 font-mono text-sm outline-none focus:border-[#007AFF]" />
            </div>
            <div className="md:col-span-2">
              <label className="label-mono block mb-2 flex items-center gap-1"><Phone size={10} className="text-[#25D366]" /> whatsapp recipients (pisahkan koma)</label>
              <input value={form.wa_recipients} onChange={(e) => setForm({...form, wa_recipients: e.target.value})}
                placeholder="6281234567890, 6289876543210"
                data-testid="schedule-wa-recipients"
                className="w-full bg-[#050505] border border-[#1F1F1F] px-3 py-2 font-mono text-sm outline-none focus:border-[#007AFF]" />
              <div className="label-mono mt-1 text-zinc-600">// minimal salah satu (email atau wa) harus diisi</div>
            </div>
          </div>
          <button type="submit" data-testid="schedule-submit"
            className="bg-[#007AFF] hover:bg-[#0056b3] px-4 py-2 label-mono">→ buat jadwal</button>
        </form>
      )}

      {items.length === 0 ? (
        <div className="border border-dashed border-[#1F1F1F] p-10 text-center label-mono text-zinc-500">
          belum ada laporan terjadwal
        </div>
      ) : (
        <div className="border border-[#1F1F1F] bg-[#0A0A0A]">
          {items.map((s, i) => (
            <div key={s.id}
              data-testid={`schedule-row-${s.id}`}
              className={`px-5 py-4 flex items-center justify-between ${i < items.length - 1 ? "border-b border-[#1F1F1F]" : ""}`}>
              <div className="flex items-center gap-3 min-w-0">
                <CalendarClock size={18} className={s.enabled ? "text-[#00E676]" : "text-zinc-600"} />
                <div className="min-w-0">
                  <div className="text-sm font-medium flex items-center gap-2">
                    {s.name}
                    {s.report_type === "ai" && (
                      <span className="label-mono text-[#00F0FF] flex items-center gap-1"><Sparkles size={9} /> ai</span>
                    )}
                  </div>
                  <div className="label-mono">
                    {s.cadence === "daily" ? "setiap hari" : `mingguan (${s.day_of_week})`} · jam {String(s.hour).padStart(2, "0")}:00 WIB · {s.format.toUpperCase()}
                    {" · "}{(s.recipients || []).length} email
                    {(s.wa_recipients || []).length > 0 && ` · ${s.wa_recipients.length} wa`}
                  </div>
                  {s.last_run_at && (
                    <div className="label-mono text-zinc-600 mt-1">last run: {new Date(s.last_run_at).toLocaleString("id-ID")}</div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => runNow(s)} title="Jalankan sekarang"
                  data-testid={`schedule-run-${s.id}`}
                  className="p-2 border border-[#1F1F1F] hover:border-[#00F0FF] hover:text-[#00F0FF] label-mono">
                  <Play size={12} />
                </button>
                <button onClick={() => toggle(s)} title="Aktif/Nonaktif"
                  data-testid={`schedule-toggle-${s.id}`}
                  className="p-2 border border-[#1F1F1F] hover:border-[#007AFF] hover:text-[#007AFF] label-mono">
                  <RefreshCw size={12} />
                </button>
                <button onClick={() => del(s)} title="Hapus"
                  data-testid={`schedule-delete-${s.id}`}
                  className="p-2 border border-[#1F1F1F] hover:border-[#FF3B30] hover:text-[#FF3B30] label-mono">
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
